import { boundsOf, hitTestRectangle } from './snap.js';

/** @typedef {{ type: 'card', id: string }} CardEndpoint */
/** @typedef {{ type: 'frame', id: string }} FrameEndpoint */
/** @typedef {CardEndpoint | FrameEndpoint} LinkEndpoint */

/** Canonical card id string (UUID or legacy int as string). Never Number() — UUID → NaN. */
export function cardRefId(raw) {
  if (raw == null || raw === '') return null;
  const s = String(raw).trim();
  return s || null;
}

export function cardRefsEqual(a, b) {
  const left = cardRefId(a);
  const right = cardRefId(b);
  if (left == null || right == null) return false;
  return left === right;
}

export function endpointFromElement(el) {
  if (!el) return null;
  if (el.type === 'card' && el.cardId != null) {
    const id = cardRefId(el.cardId);
    return id ? { type: 'card', id } : null;
  }
  if (el.type === 'frame' && el.id) {
    return { type: 'frame', id: el.id };
  }
  return null;
}

export function endpointsEqual(a, b) {
  if (!a || !b) return false;
  return a.type === b.type && String(a.id) === String(b.id);
}

export function resolveEndpoint(endpoint, elements) {
  if (!endpoint) return null;
  if (endpoint.type === 'card') {
    return elements.find(
      (e) => e.type === 'card' && cardRefsEqual(e.cardId, endpoint.id),
    );
  }
  if (endpoint.type === 'frame') {
    return elements.find((e) => e.type === 'frame' && e.id === endpoint.id);
  }
  return null;
}

export function endpointCenter(el) {
  const b = boundsOf(el);
  return { x: b.cx, y: b.cy };
}

/** 从元素边界向目标点方向的连线锚点 */
export function linkAnchorPoint(el, towardX, towardY) {
  const b = boundsOf(el);
  const cx = b.cx;
  const cy = b.cy;
  const dx = towardX - cx;
  const dy = towardY - cy;
  if (dx === 0 && dy === 0) return [cx, cy];
  const hw = (b.right - b.left) / 2;
  const hh = (b.bottom - b.top) / 2;
  if (hw <= 0 || hh <= 0) return [cx, cy];
  const scale = 1 / Math.max(Math.abs(dx) / hw, Math.abs(dy) / hh);
  return [cx + dx * scale, cy + dy * scale];
}

export function updateArrowEndpoints(arrow, elements) {
  if (!arrow || arrow.type !== 'arrow' || !arrow.points?.length) return false;
  const srcRef = arrow.sourceFrameId
    ? { type: 'frame', id: arrow.sourceFrameId }
    : arrow.sourceCardId != null
      ? { type: 'card', id: cardRefId(arrow.sourceCardId) }
      : null;
  const tgtRef = arrow.targetFrameId
    ? { type: 'frame', id: arrow.targetFrameId }
    : arrow.targetCardId != null
      ? { type: 'card', id: cardRefId(arrow.targetCardId) }
      : null;
  if (!srcRef?.id || !tgtRef?.id) return false;
  const src = resolveEndpoint(srcRef, elements);
  const tgt = resolveEndpoint(tgtRef, elements);
  if (!src || !tgt) return false;
  const tc = endpointCenter(tgt);
  const sc = endpointCenter(src);
  const a = linkAnchorPoint(src, tc.x, tc.y);
  const b = linkAnchorPoint(tgt, sc.x, sc.y);
  arrow.points[0] = a;
  arrow.points[1] = b;
  return true;
}

export function syncRelationArrows(elements) {
  for (const el of elements) {
    if (el.type !== 'arrow') continue;
    updateArrowEndpoints(el, elements);
  }
}

export function arrowMatchesEndpoints(arrow, source, target) {
  if (!arrow || arrow.type !== 'arrow') return false;
  const srcRef = arrow.sourceFrameId
    ? { type: 'frame', id: arrow.sourceFrameId }
    : arrow.sourceCardId != null
      ? { type: 'card', id: cardRefId(arrow.sourceCardId) }
      : null;
  const tgtRef = arrow.targetFrameId
    ? { type: 'frame', id: arrow.targetFrameId }
    : arrow.targetCardId != null
      ? { type: 'card', id: cardRefId(arrow.targetCardId) }
      : null;
  return endpointsEqual(srcRef, source) && endpointsEqual(tgtRef, target);
}

export function pickLinkEndpoint(elements, wx, wy, pickElementAt) {
  const hit = pickElementAt(elements, wx, wy);
  const fromHit = endpointFromElement(hit);
  if (fromHit) return hit;
  for (const el of elements) {
    if (el.type !== 'frame') continue;
    if (hitTestRectangle(el, wx, wy, 4)) return el;
  }
  return null;
}
